import type { UseFormReturn } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { MetadataSelect } from "@/components/itsm-config/metadata-select";
import { useSubdesks } from "@/hooks/use-desk-migration";
import { useSubdeskName } from "@/hooks/use-itsm-labels";
import { teamOptions } from "@/lib/itsm-config/export-rule-team";

// An export rule can narrow its desk to one team (subdesk). The backend
// requires the desk with it, since subdesk ids are only unique within a desk,
// and ranks a team rule above the desk's desk-only rule.

/** The rule table's "Team" line under the desk name. */
export function TeamLine({
  tenantId,
  deskId,
  subdeskId,
}: {
  tenantId: string;
  deskId?: string | null;
  subdeskId?: string | null;
}) {
  const name = useSubdeskName(tenantId, deskId, subdeskId);
  // A team only counts alongside its desk; the next save drops it otherwise.
  if (!subdeskId || !deskId) return null;
  return (
    <div className="text-xs">
      <span className="text-muted-foreground">Team: </span>
      {name}
    </div>
  );
}

/** The dialog's Team picker; only enabled once a desk is chosen. */
export function TeamField({
  form,
  tenantId,
  deskId,
}: {
  form: UseFormReturn<{ subdesk_id?: string }>;
  tenantId: string;
  deskId?: string;
}) {
  const subdesks = useSubdesks(tenantId, deskId ?? "");
  const options = teamOptions(subdesks.data);
  const noTeams = Boolean(deskId) && subdesks.isSuccess && options.length <= 1;
  const hint = !deskId
    ? "Pick a desk first"
    : subdesks.isError
      ? "Couldn't load this desk's teams"
      : noTeams
        ? "This desk has no teams"
        : "Any team";

  return (
    <FormField
      control={form.control}
      name="subdesk_id"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Team (optional)</FormLabel>
          <FormControl>
            <MetadataSelect
              options={options}
              isLoading={subdesks.isLoading}
              value={field.value ?? ""}
              onChange={field.onChange}
              allowCustom={false}
              // A saved team stays clearable even once its desk has none left.
              disabled={
                !deskId || ((noTeams || subdesks.isError) && !field.value)
              }
              placeholder={hint}
            />
          </FormControl>
          <FormDescription>
            {hint === "Any team"
              ? "Only tickets assigned to this team. Outranks the desk's " +
                "rule without a team."
              : `${hint}. Only tickets assigned to the chosen team match.`}
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
